import React, { useState, useEffect } from "react";
import { requestOtp, verifyOtp, checkUserExists } from "../api";
import { useNavigate } from "react-router-dom";
import {
  isValidMobile,
  isValidOtp,
  generateIdentityKeys,
  storeLoginData
} from "./loginUtils";

export default function Login() {
  const [mobile, setMobile] = useState("");
  const [name, setName] = useState(localStorage.getItem("name") || "");
  const [otp, setOtp] = useState("");
  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [userExists, setUserExists] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    const token = localStorage.getItem("token");
    if (token && window.location.pathname === "/") {
      navigate("/users");
    }
  }, [navigate]);

  const handleMobileSubmit = async () => {
    setError("");
    if (!isValidMobile(mobile)) {
      setError("Enter a valid 10-digit mobile number");
      return;
    }

    setLoading(true);
    try {
      const res = await checkUserExists(mobile);
      const exists = res.data.exists;
      const fetchedName = res.data.name || "";
      setUserExists(exists);
      setName(fetchedName);
      if (fetchedName) localStorage.setItem("name", fetchedName);

      if (exists) {
        await requestOtp(mobile, fetchedName || undefined); // ✅ Request OTP for existing user
        setStep(3);
      } else {
        setStep(2); // New user: enter name, then request OTP
      }
    } catch {
      setError("Failed to check user. Try again.");
    } finally {
      setLoading(false);
    }
  };

  const handleRequestOtp = async () => {
    setError("");
    if (!name.trim()) {
      setError("Please enter your name");
      return;
    }

    setLoading(true);
    try {
      await requestOtp(mobile, name);
      localStorage.setItem("name", name);
      setStep(3);
    } catch {
      setError("Failed to send OTP. Try again.");
    } finally {
      setLoading(false);
    }
  };

  const handleVerify = async () => {
    setError("");
    if (!isValidOtp(otp)) {
      setError("Enter a valid 6-digit OTP");
      return;
    }

    setLoading(true);
    try {
      let identityPrivateKeyBase64 = localStorage.getItem("identityPrivateKey");
      let identityPublicKeyBase64 = localStorage.getItem("identityPublicKey");

      if (!identityPrivateKeyBase64 || !identityPublicKeyBase64) {
        const keys = await generateIdentityKeys();
        identityPrivateKeyBase64 = keys.identityPrivateKeyBase64;
        identityPublicKeyBase64 = keys.identityPublicKeyBase64;

        localStorage.setItem("identityPrivateKey", identityPrivateKeyBase64);
        localStorage.setItem("identityPublicKey", identityPublicKeyBase64);
      }

      const res = await verifyOtp(mobile, otp, name, identityPublicKeyBase64);
      const token = res.data.token;
      if (!token) throw new Error("Missing token");

      storeLoginData({ token, mobile, name });
      navigate("/users");
    } catch {
      setError("Invalid OTP. Try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="container mt-5" style={{ maxWidth: 400 }}>
      <h2 className="mb-4 text-center">Login</h2>

      {step === 1 && (
        <>
          <div className="mb-3">
            <label className="form-label">Mobile Number</label>
            <input
              type="text"
              className="form-control"
              value={mobile}
              onChange={(e) => setMobile(e.target.value)}
              placeholder="Enter mobile number"
            />
          </div>
          <button
            className="btn btn-primary w-100"
            onClick={handleMobileSubmit}
            disabled={loading}
          >
            {loading ? "Checking..." : "Next"}
          </button>
        </>
      )}

      {step === 2 && (
        <>
          <div className="mb-3">
            <label className="form-label">Mobile Number</label>
            <input type="text" className="form-control" value={mobile} disabled />
          </div>
          <div className="mb-3">
            <label className="form-label">Your Name</label>
            <input
              type="text"
              className="form-control"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Enter your name"
            />
          </div>
          <button
            className="btn btn-success w-100"
            onClick={handleRequestOtp}
            disabled={loading}
          >
            {loading ? "Sending OTP..." : "Request OTP"}
          </button>
        </>
      )}

      {step === 3 && (
        <>
          {userExists && (
            <div className="alert alert-success text-center">
              Welcome back, <strong>{name}</strong>! Please enter your OTP to continue.
            </div>
          )}
          <div className="mb-3">
            <label className="form-label">Mobile Number</label>
            <input type="text" className="form-control" value={mobile} disabled />
          </div>
          <div className="mb-3">
            <label className="form-label">Your Name</label>
            <input type="text" className="form-control" value={name} disabled />
          </div>
          <div className="mb-3">
            <label className="form-label">OTP</label>
            <input
              type="text"
              className="form-control"
              value={otp}
              onChange={(e) => setOtp(e.target.value)}
              placeholder="Enter OTP"
            />
          </div>
          <button
            className="btn btn-dark w-100"
            onClick={handleVerify}
            disabled={loading}
          >
            {loading ? "Verifying..." : "Verify OTP"}
          </button>
        </>
      )}

      {error && <div className="alert alert-danger mt-3">{error}</div>}
    </div>
  );
}
